import { useEffect, useRef, useState } from 'react'
import { Button, Loader, Modal } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import type { Patient } from '@medplum/fhirtypes'
import { IconAlertTriangle } from '@tabler/icons-react'
import { CreateUpdatePatientModal } from '@/components/ui/CreateUpdatePatientModal'
import { DoseSpotLaunchFailurePanel } from '@/components/DoseSpotLaunchFailurePanel'
import { useOrpc } from '@/hooks/useClient'
import { formatPatientName } from '@/utils.ts'
import { describeDoseSpotLaunchFailure } from './doseSpotLaunchFailure'
import { usePatientSidePanel } from './PatientSidePanelContext'

/**
 * When the interstitial moves to its second line. The token mint alone measures
 * ~800 ms and the patient write ~900 ms, so this is roughly where the flow really
 * gets to the patient sync — the two steps are a truthful account of a 2–4 s wait,
 * not a spinner dressed up.
 */
const SYNC_STEP_DELAY_MS = 900

type Props = {
  patient: Patient
  /**
   * Round-trips onto the prescription. Present only on the call route (it comes
   * from that route's own match), which makes it the in-call signal too.
   */
  encounterId?: string
  onClose: () => void
  /** The chart holds the patient in parent state; a demographics edit must refresh it. */
  onPatientEdited?: () => void
}

/**
 * The prescribing surface: one Mantine modal that holds the whole launch path —
 * the interstitial, every refusal, and DoseSpot's own app in an iframe.
 *
 * Mounted only while open (the caller conditionally renders it) so closing
 * genuinely discards the minted URL. That is a safety property, not tidiness: SSO
 * codes are single-use, so a URL surviving a close would be replayed into a
 * guaranteed failure, and a URL that never reaches a query param, a route, or
 * SSR'd HTML cannot be resurrected by a reload or the back button either.
 *
 * **No `message` listener, on purpose.** The embedded app broadcasts a continuous
 * rrweb DOM mirror — patient name, medications, allergies, and SSN and card fields
 * on the identity-proofing screen — to the parent window. It is undocumented
 * third-party analytics, not an integration API, and BeHuman must not consume it.
 * Likewise no overlays masking DoseSpot's chrome: the certified app has to render
 * unmodified.
 */
export const DoseSpotPrescribeModal = ({
  patient,
  encounterId,
  onClose,
  onPatientEdited,
}: Props) => {
  const medplum = useMedplum()
  const orpc = useOrpc()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const savedInEditorRef = useRef(false)
  const { emitPanelEvent } = usePatientSidePanel()
  const [isSyncing, setIsSyncing] = useState(false)
  const patientId = patient.id as string

  const launch = useMutation(orpc.doseSpotLaunch.mutationOptions())

  const startLaunch = () => {
    setIsSyncing(false)
    launch.mutate({ patientId, ...(encounterId && { encounterId }) })
  }

  // Mint on mount, and only here: this component's lifetime is one launch.
  useEffect(() => {
    startLaunch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!launch.isPending) return
    const timer = setTimeout(() => setIsSyncing(true), SYNC_STEP_DELAY_MS)
    return () => clearTimeout(timer)
  }, [launch.isPending])

  const patientName = formatPatientName(patient.name?.[0])
  const failure = launch.isError ? describeDoseSpotLaunchFailure(launch.error) : null

  return (
    <>
      <Modal
        opened
        onClose={onClose}
        // Ordinary close and "Done prescribing" are the same exit, deliberately:
        // there is no observable "finished" event from a cross-origin frame, so
        // both run the one handler — which is also where the write-back sync of
        // whatever was just prescribed hangs off.
        size="1100px"
        // The stacking scale lives in index.css alongside the scribe rail's.
        zIndex={'var(--z-dosespot-modal)' as unknown as number}
        // A stray click outside must not end a part-written prescription, nor —
        // once the write-back sync hangs off the exit — fire it by accident.
        closeOnClickOutside={false}
        title={
          /*
           * The name is not chased. A prescriber can click DoseSpot's own logo and
           * wander off this patient's record into its Tasks view, and the frame is
           * cross-origin — the only channel that would reveal it is the rrweb
           * broadcast the spec forbids listening to. So the header states which
           * patient this session was *launched for*, which stays true either way.
           */
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold">Prescribe · {patientName}</span>
            <Button size="xs" variant="light" onClick={onClose}>
              Done prescribing
            </Button>
          </div>
        }
      >
        {/*
         * Above the state branches, not inside the frame's: the modal covers the
         * video from the moment it opens, including through the interstitial and
         * every refusal.
         */}
        {encounterId && (
          <div className="mb-2 text-xs text-gray-500">
            Call in progress — audio continues, the video is hidden while this window is open.
          </div>
        )}

        {launch.isPending && (
          <div className="flex flex-col items-center gap-3 py-24">
            <Loader size="sm" />
            <div className="text-sm font-medium">
              {isSyncing
                ? `Syncing ${patientName}'s record to DoseSpot…`
                : 'Connecting to DoseSpot…'}
            </div>
            <div className="text-xs text-gray-500">Usually takes a couple of seconds.</div>
          </div>
        )}

        {failure && (
          // The refusal states are shared verbatim with the bell's patient-less launch — a
          // blocked provider must not read two accounts of the same refusal. The fix path is
          // the one thing only this surface has: it needs a patient to edit.
          <DoseSpotLaunchFailurePanel
            failure={failure}
            onRetry={startLaunch}
            onFix={() => setIsEditOpen(true)}
            onClose={onClose}
          />
        )}

        {launch.data && (
          <div className="flex flex-col gap-2">
            {launch.data.allergyWarning && (
              <div className="flex items-start gap-2 rounded border border-orange-200 bg-orange-50 p-2 text-sm text-orange-900">
                <IconAlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
                <span>
                  Allergies could not be synced; DoseSpot&apos;s allergy checking may be incomplete
                  — verify against the chart
                  {launch.data.allergyWarning.failed.length > 0 &&
                    `: ${launch.data.allergyWarning.failed.join(', ')}`}
                  .
                </span>
              </div>
            )}
            {/*
             * DoseSpot's one-time setup wall (its agreement plus Experian identity
             * proofing) is a *success* that lands nowhere: nothing in the SSO result
             * or the error vocabulary distinguishes it, and the frame is opaque. So
             * this says the true thing in both worlds instead of a banner that would
             * claim "one-time setup" on every launch forever.
             */}
            <div className="text-xs text-gray-500">
              If DoseSpot asks you to accept its agreement or verify your identity, you can finish
              that here. Closing this window loses nothing.
            </div>
            {/*
             * The 600px floor is the smallest viewport DoseSpot's app was verified
             * responsive at, and the identity-proofing wall has to be completable in
             * place — so the frame never shrinks below what that flow needs.
             */}
            <iframe
              title="DoseSpot prescribing"
              src={launch.data.url}
              className="w-full"
              style={{ height: 'calc(100vh - 260px)', minHeight: 600, border: 0 }}
            />
          </div>
        )}
      </Modal>

      {isEditOpen && (
        <CreateUpdatePatientModal
          isOpen
          onClose={() => {
            setIsEditOpen(false)
            // As on the chart's Edit Patient: Demographics re-reads its insurance only if
            // something was saved in the editor, including a plan or request in its insurance section.
            if (savedInEditorRef.current) {
              savedInEditorRef.current = false
              emitPanelEvent('patient:edited')
            }
          }}
          patient={patient}
          onInsuranceChanged={() => {
            savedInEditorRef.current = true
          }}
          successCallback={() => {
            savedInEditorRef.current = true
            medplum.invalidateSearches('Patient')
            onPatientEdited?.()
            // The chart is the fix path, so a save is the retry.
            startLaunch()
          }}
        />
      )}
    </>
  )
}
